import { getApiBaseUrl } from "../utils/constants";
import { getAuthToken } from "./cookie-manager";

export type Pagination = {
    has_more: boolean;
    next_page: number;
    previous_page: number;
    current_page: number;
    per_page: number;
    total_items: number;
    total_pages: number;
    limit: number;
};

export type ApiResponse<T> = {
    data: T | null;
    status: boolean;
    message: string;
    pagination?: Pagination;
};

type ApiParams = {
    url: string;
    queryParams?: Record<string, string>;
    body?: Record<string, unknown>;
    headers?: HeadersInit;
    signal?: AbortSignal;
    options?: RequestInit;
    revalidate?: number;
};

function appendQueryParams(url: string, queryParams: Record<string, string>): string {
    var parts: string[] = [];
    var key: string;
    for (key in queryParams) {
        if (Object.prototype.hasOwnProperty.call(queryParams, key)) {
            parts.push(encodeURIComponent(key) + "=" + encodeURIComponent(queryParams[key]));
        }
    }
    if (parts.length === 0) {
        return url;
    }
    return url + "?" + parts.join("&");
}

type ApiSendParams = ApiParams & { method: "POST" | "PUT" | "DELETE" | "GET" };

function withMethod(method: ApiSendParams["method"], params: ApiParams): ApiSendParams {
    return {
        url: params.url,
        queryParams: params.queryParams,
        body: params.body,
        headers: params.headers,
        signal: params.signal,
        options: params.options,
        revalidate: params.revalidate,
        method: method,
    };
}

function normalizeHeadersInput(headers?: HeadersInit): Record<string, string> {
    var result: Record<string, string> = {};
    if (!headers) {
        return result;
    }

    if (typeof Headers !== "undefined" && headers instanceof Headers) {
        headers.forEach(function (value, key) {
            result[key] = value;
        });
        return result;
    }

    if (Object.prototype.toString.call(headers) === "[object Array]") {
        var pairs = headers as Array<[string, string]>;
        for (var i = 0; i < pairs.length; i = i + 1) {
            var pair = pairs[i];
            if (pair && pair.length >= 2) {
                result[String(pair[0])] = String(pair[1]);
            }
        }
        return result;
    }

    var record = headers as Record<string, string>;
    for (var name in record) {
        if (Object.prototype.hasOwnProperty.call(record, name)) {
            result[name] = String(record[name]);
        }
    }
    return result;
}

export const api = {
    async get<T>(params: ApiParams): Promise<ApiResponse<T>> {
        return this.send<T>(withMethod("GET", params));
    },

    async post<T>(params: ApiParams): Promise<ApiResponse<T>> {
        return this.send<T>(withMethod("POST", params));
    },

    async put<T>(params: ApiParams): Promise<ApiResponse<T>> {
        return this.send<T>(withMethod("PUT", params));
    },

    async delete<T>(params: ApiParams): Promise<ApiResponse<T>> {
        return this.send<T>(withMethod("DELETE", params));
    },

    async send<T>(params: ApiSendParams): Promise<ApiResponse<T>> {
        let requestUrl = getApiBaseUrl() + params.url;
        var requestHeaders = normalizeHeadersInput(params.headers);

        if (params.queryParams) {
            requestUrl = appendQueryParams(requestUrl, params.queryParams);
        }

        var authToken = getAuthToken();
        if (authToken && !requestHeaders.Authorization && !requestHeaders.authorization) {
            requestHeaders.Authorization = "Bearer " + authToken;
        }

        var hasBody = params.body !== undefined && params.body !== null;
        var fetchHeaders: Record<string, string> = Object.assign({}, requestHeaders);
        if (hasBody) {
            fetchHeaders["Content-Type"] = "application/json";
        }

        const res = await fetch(requestUrl, Object.assign({}, params.options, {
            headers: fetchHeaders,
            method: params.method,
            body: hasBody ? JSON.stringify(params.body) : undefined,
            credentials: "include",
            signal: params.signal || undefined,
        }));

        return this.parseResponse<T>(res);
    },

    async parseResponse<T>(res: Response): Promise<ApiResponse<T>> {
        if (res.status === 204) {
            return {
                data: null,
                status: false,
                message: "مشکلی در برقراری ارتباط با سرور وجود دارد",
            };
        }

        var responseText = await res.text();
        if (!responseText) {
            return {
                data: null,
                status: false,
                message: "مشکلی در برقراری ارتباط با سرور وجود دارد",
            };
        }

        var dataJson: { status: boolean; message: string | null; data: T | null; pagination?: Pagination };
        try {
            dataJson = JSON.parse(responseText);
        } catch (parseError) {
            return {
                data: null,
                status: false,
                message: "مشکلی در برقراری ارتباط با سرور وجود دارد",
            };
        }

        if (!res.ok) {
            return {
                data: dataJson.data !== undefined && dataJson.data !== null ? dataJson.data : null,
                status: false,
                message: dataJson.message || "مشکلی در برقراری ارتباط با سرور وجود دارد",
            };
        }

        const status = dataJson.status as boolean;

        if (!status) {
            const message = dataJson.message as string;
            return {
                data: dataJson.data !== undefined && dataJson.data !== null ? dataJson.data : null,
                status: status,
                message: message,
            };
        }

        const data = dataJson.data as T;
        const pagination = dataJson.pagination as Pagination;

        return {
            data: data,
            status: status,
            message: "",
            pagination: pagination,
        };
    },
};
